import { useTranslation } from "react-i18next";
import type { Review, ShapeViolation } from "../../../api/content/reviewDecoders";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import { useConflictTermNames } from "../conflicts/conflictTermNames";
import { checkNames } from "../state/mergeBlockReport";
import CheckFindingView from "../checks/CheckFindingView";
import { withItemNames } from "../checks/checkFindings";

/** R-3: explain the server's block before a decision; keep baseline violations apart. */
export default function MergeBlocksNotice({ review, technical }: { review: Review; technical: boolean }) {
  const { t } = useTranslation();
  const blocks = review.mergeBlocks;
  const shapes = blocks.shapeViolations;
  const labels = withItemNames(useConflictTermNames(review.conflictCheck), review.items, [...shapes.introduced, ...shapes.preExisting]);
  const checks = checkNames(review, blocks.inconsistencyCheckIds);
  const uncompared = checkNames(review, shapes.uncomparedCheckIds);
  if (!blocks.blocked && shapes.preExisting.length === 0 && uncompared.length === 0) return null;
  const violations = (entries: readonly ShapeViolation[]) => (
    <ul className="space-y-2">
      {entries.map((entry, index) => (
        <li key={`${entry.checkId}:${String(index)}`}>
          <span className="font-medium">{entry.checkTitle ?? entry.checkId}</span>{" — "}
          <CheckFindingView finding={entry.finding} labels={labels} technical={technical} />
        </li>
      ))}
    </ul>
  );
  return (
    <StatusMessage tone={blocks.blocked ? "error" : "info"} title={blocks.blocked ? t("reviews.mergeBlocks.title") : undefined} className="space-y-2 text-sm">
      {blocks.blocked ? (
        <div className="space-y-2">
          <p>{t("reviews.mergeBlocks.explanation")}</p>
          {checks.length > 0 ? (
            <div>
              <h3 className="font-semibold">{t("reviews.mergeBlocks.inconsistent")}</h3>
              <ul className="list-disc pl-5">{checks.map((name, index) => <li key={index}>{name}</li>)}</ul>
            </div>
          ) : null}
          {shapes.introduced.length > 0 || shapes.unlistedIntroduced > 0 ? (
            <div>
              <h3 className="font-semibold">{t("reviews.mergeBlocks.introduced")}</h3>
              {violations(shapes.introduced)}
              {shapes.unlistedIntroduced > 0 ? <p>{t("reviews.mergeBlocks.more", { count: shapes.unlistedIntroduced })}</p> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {uncompared.length > 0 ? <p>{t("reviews.mergeBlocks.uncompared", { checks: uncompared.join(", ") })}</p> : null}
      {shapes.preExisting.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer font-medium">{t("reviews.mergeBlocks.preExisting")}</summary>
          {violations(shapes.preExisting)}
        </details>
      ) : null}
    </StatusMessage>
  );
}
